import cx from "classnames";
import { useAtom, useAtomValue } from "jotai/index";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canv } from "@/canv/global";
import type { PresentationWidget } from "@/elements/Presentation/Presentation";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { UsersChevronIcon } from "@/icons";
import { presentationUID, usersAtom } from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar/BottomRightBar";
import { isMobile } from "@/utils";
import styles from "./PresentationMembers.module.scss";
import { UserMonets } from "./UserMonets";

export const PresentationMembers = ({ blackBackground }: { blackBackground?: boolean }) => {
    const { users } = useAtomValue(usersAtom);
    const [UID] = useAtom(presentationUID);

    const [viewerTokens, setViewerTokens] = useState<string[]>([]);
    const [presenterToken, setPresenterToken] = useState<string>("");

    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const showMultiLimit = isMobile() ? 2 : 4;

    const currentUserToken = canv.getInitDataProp("userToken");
    const currentUserName = canv.getInitDataProp("userName");

    const openUsersWindow = () => {
        setIsOpen((prev) => !prev);
    };

    const getPresentationData = useCallback((UID: string) => {
        const presentation = canv.canvasController.FindByUID(UID) as PresentationWidget;
        if (presentation) {
            setViewerTokens(presentation.viewerUIDs);
            setPresenterToken(presentation.presenterToken);
        } else {
            setViewerTokens([]);
            setPresenterToken("");
        }
    }, []);

    useEffect(() => {
        getPresentationData(UID);
    }, [UID, getPresentationData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const handler = () => getPresentationData(UID);

        document.addEventListener("updatePresentationMembersCountMessage", handler);
        return () => {
            document.removeEventListener("updatePresentationMembersCountMessage", handler);
        };
    }, [getPresentationData, UID]);

    const viewers = useMemo(() => {
        return users.filter((u) => viewerTokens.includes(u.token as string));
    }, [users, viewerTokens]);

    const renderViewer = useCallback(
        (user: (typeof users)[number]) => {
            if (!viewerTokens.includes(user.token as string)) return null;

            return (
                <div key={user.sessionUID} className={styles.dropdownUser}>
                    <div
                        className={cx(
                            styles.barPanelRightUsersItemView,
                            presenterToken === user.token &&
                                styles.barPanelRightUsersItemIndicatorSelected,
                        )}
                        style={{
                            backgroundColor: generateColor(user.token || user.sessionUID),
                        }}
                    >
                        {getInitials(user.name)}
                    </div>

                    <span>{user.name}</span>
                </div>
            );
        },
        [viewerTokens, presenterToken],
    );

    return (
        <div ref={wrapperRef} className={styles.usersWrapper}>
            <BottomBarItem
                className={cx(styles.usersList, blackBackground ? styles.whiteChevron : "")}
                onClick={openUsersWindow}
            >
                <span className={styles.barPanelRightUsers}>
                    <UserMonets
                        users={users}
                        viewerTokens={viewerTokens}
                        presenterToken={presenterToken}
                        currentUserToken={currentUserToken}
                        currentUserName={currentUserName}
                        showLimit={showMultiLimit}
                    />
                </span>
                {users.length > showMultiLimit && (
                    <div className="barPanelRightUsersItemViewMore">
                        +{users.length - showMultiLimit}
                    </div>
                )}
                <UsersChevronIcon />
            </BottomBarItem>
            {isOpen && (
                <div className={styles.usersDropdown}>
                    <div className={styles.usersDropdownCount}> Пользователи: {viewers.length}</div>
                    <div className={styles.divider}></div>

                    {viewers.map(renderViewer)}
                </div>
            )}
        </div>
    );
};
